import sodium from 'libsodium-wrappers-sumo';

/**
 * Primitives cryptographiques de LifeHub : uniquement libsodium (variante « sumo », la seule qui expose
 * Argon2id), selon Docs/crypto/crypto.md. Aucun algorithme maison. Les clés ne vivent qu'en mémoire.
 */
export class CryptoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CryptoError';
  }
}

export const BLOB_VERSION = 0x01;
export const KEY_BYTES = 32;
const NONCE_BYTES = 24;
const TAG_BYTES = 16;

let initialised: Promise<void> | null = null;
export function sodiumReady(): Promise<void> {
  initialised ??= sodium.ready;
  return initialised;
}

const utf8 = (text: string): Uint8Array => sodium.from_string(text);

/** AAD : `tag ‖ (0x00 ‖ partie)*`, en UTF-8. Lie un blob à son contexte (objet, version de clé, type, rôle). */
export function aad(tag: string, ...parts: Array<string | number>): Uint8Array {
  const chunks: Uint8Array[] = [utf8(tag)];
  for (const part of parts) {
    chunks.push(Uint8Array.of(0), utf8(String(part)));
  }
  return concatBytes(...chunks);
}

/** Blob v1 : `0x01 ‖ nonce(24) ‖ XChaCha20-Poly1305(clé, nonce, aad)(clair)`. Le nonce est aléatoire sauf dans les vecteurs. */
export function encryptBlob(key: Uint8Array, plaintext: Uint8Array, context: Uint8Array, nonce?: Uint8Array): Uint8Array {
  if (key.length !== KEY_BYTES) throw new CryptoError('clé de 32 octets attendue');
  const n = nonce ?? sodium.randombytes_buf(NONCE_BYTES);
  if (n.length !== NONCE_BYTES) throw new CryptoError('nonce de 24 octets attendu');
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, context, null, n, key);
  return concatBytes(Uint8Array.of(BLOB_VERSION), n, ciphertext);
}

/** Déchiffre un blob v1 ; toute anomalie (version, taille, clé, contexte) donne la même erreur, sans détail. */
export function decryptBlob(key: Uint8Array, blob: Uint8Array, context: Uint8Array): Uint8Array {
  if (blob.length < 1 + NONCE_BYTES + TAG_BYTES || blob[0] !== BLOB_VERSION || key.length !== KEY_BYTES) {
    throw new CryptoError('blob illisible');
  }
  try {
    return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      blob.subarray(1 + NONCE_BYTES),
      context,
      blob.subarray(1, 1 + NONCE_BYTES),
      key,
    );
  } catch {
    throw new CryptoError('blob illisible');
  }
}

export const encryptText = (key: Uint8Array, text: string, context: Uint8Array): Uint8Array =>
  encryptBlob(key, utf8(text), context);

export const decryptText = (key: Uint8Array, blob: Uint8Array, context: Uint8Array): string =>
  sodium.to_string(decryptBlob(key, blob, context));

export const randomKey = (): Uint8Array => sodium.randombytes_buf(KEY_BYTES);
export const randomBytes = (n: number): Uint8Array => sodium.randombytes_buf(n);

/** Entier uniforme dans [0, bound[ sans biais de modulo (libsodium) ; réservé au générateur de mots de passe. */
export const randomUniform = (bound: number): number => {
  if (!Number.isInteger(bound) || bound < 1 || bound > 0xffffffff) throw new CryptoError('borne invalide');
  return sodium.randombytes_uniform(bound);
};

export interface KeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

export const generateKeyPair = (): KeyPair => {
  const pair = sodium.crypto_box_keypair();
  return { publicKey: pair.publicKey, privateKey: pair.privateKey };
};

export const keyPairFromSeed = (seed: Uint8Array): KeyPair => {
  const pair = sodium.crypto_box_seed_keypair(seed);
  return { publicKey: pair.publicKey, privateKey: pair.privateKey };
};

/** Chiffre vers une clé publique (X25519, `crypto_box_seal`) : seul le détenteur de la clé privée peut ouvrir. */
export const sealFor = (publicKey: Uint8Array, message: Uint8Array): Uint8Array => sodium.crypto_box_seal(message, publicKey);

export function openSealed(pair: KeyPair, sealed: Uint8Array): Uint8Array {
  try {
    return sodium.crypto_box_seal_open(sealed, pair.publicKey, pair.privateKey);
  } catch {
    throw new CryptoError('clé scellée illisible');
  }
}

/** Condensat BLAKE2b de longueur choisie (empreintes de clés publiques ; jamais pour des mots de passe). */
export const genericHash = (data: Uint8Array, length: number): Uint8Array => sodium.crypto_generichash(length, data, null);

export interface KdfParams {
  alg: 'argon2id';
  /** Sel de 16 octets (libsodium). */
  salt: Uint8Array;
  /** Mémoire en KiB. */
  m: number;
  t: number;
  /** libsodium fixe le parallélisme à 1. */
  p: 1;
}

export const DEFAULT_KDF = { m: 65536, t: 3 } as const;

export function newKdfParams(overrides: Partial<Pick<KdfParams, 'm' | 't'>> = {}): KdfParams {
  return { alg: 'argon2id', salt: randomBytes(16), m: overrides.m ?? DEFAULT_KDF.m, t: overrides.t ?? DEFAULT_KDF.t, p: 1 };
}

/** Argon2id → 32 octets. Lent par conception : à appeler hors du fil principal (voir kdf.ts). */
export function argon2Master(password: string, params: KdfParams): Uint8Array {
  if (params.alg !== 'argon2id' || params.p !== 1 || params.salt.length !== 16) {
    throw new CryptoError('paramètres Argon2id non pris en charge');
  }
  return sodium.crypto_pwhash(KEY_BYTES, utf8(password), params.salt, params.t, params.m * 1024, sodium.crypto_pwhash_ALG_ARGON2ID13);
}

export const KDF_CONTEXTS = {
  kek: { id: 1, context: 'LHKEK001' },
  authKey: { id: 2, context: 'LHAUTH01' },
} as const;

export const deriveSubKey = (master: Uint8Array, which: keyof typeof KDF_CONTEXTS): Uint8Array =>
  sodium.crypto_kdf_derive_from_key(KEY_BYTES, KDF_CONTEXTS[which].id, KDF_CONTEXTS[which].context, master);

/** Met à zéro des tampons de clés dès qu'ils ne servent plus. */
export function wipe(...buffers: Array<Uint8Array | undefined>): void {
  for (const b of buffers) if (b) sodium.memzero(b);
}

export const toBase64 = (bytes: Uint8Array): string => sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
export const fromBase64 = (text: string): Uint8Array => sodium.from_base64(text, sodium.base64_variants.ORIGINAL);
export const toHex = (bytes: Uint8Array): string => sodium.to_hex(bytes);
export const fromHex = (hex: string): Uint8Array => sodium.from_hex(hex);

export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}
